// File: webelieve-frontend/src/features/notes/components/NoteCollaborationToolbar.tsx
// Compact collaboration toolbar for note editor header

import React, { useEffect, useMemo } from 'react';
import { useNoteCollaborationStore } from '../stores/useNoteCollaborationStore';

interface NotePresenceUser {
  user_id: string;
  user_email?: string;
  timestamp?: number;
  chat_ping_until?: number;
}

interface ToolbarAvatarUser {
  key: string;
  label: string;
  isActive: boolean;
  isChatPing?: boolean;
}

interface NoteCollaborationToolbarProps {
  noteId: string;
  confidentiality: string;
  onCommentsToggle: () => void;
  commentsOpen: boolean;
  onShareClick: () => void;
  onSettingsClick: () => void;
  activePresenceCount?: number;
  realtimeConnected?: boolean;
  activeUsers?: NotePresenceUser[];
}

const AVATAR_COLORS = [
  '#2563EB',
  '#16A34A',
  '#DC2626',
  '#EA580C',
  '#7C3AED',
  '#0891B2',
  '#BE123C',
  '#0D9488',
  '#4F46E5',
  '#65A30D',
];

function hashKey(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getCurrentUserAvatar(): ToolbarAvatarUser | null {
  const parsedToken = (window as any)?.keycloak?.tokenParsed;
  const parsedSub = typeof parsedToken?.sub === 'string' ? parsedToken.sub : '';
  const parsedEmail = typeof parsedToken?.email === 'string'
    ? parsedToken.email
    : (typeof parsedToken?.preferred_username === 'string' ? parsedToken.preferred_username : '');
  const parsedName = typeof parsedToken?.name === 'string' ? parsedToken.name : '';

  if (parsedSub || parsedEmail || parsedName) {
    return {
      key: parsedSub || parsedEmail || parsedName,
      label: parsedName || parsedEmail || 'You',
      isActive: true,
    };
  }

  const storageToken =
    localStorage.getItem('webelieve_access_token')
    || localStorage.getItem('token')
    || sessionStorage.getItem('webelieve_access_token')
    || sessionStorage.getItem('token');

  if (!storageToken) return null;

  try {
    const payload = JSON.parse(atob(storageToken.split('.')[1] || ''));
    const sub = typeof payload?.sub === 'string' ? payload.sub : '';
    const email = typeof payload?.email === 'string'
      ? payload.email
      : (typeof payload?.preferred_username === 'string' ? payload.preferred_username : '');
    const name = typeof payload?.name === 'string' ? payload.name : '';

    if (!sub && !email && !name) return null;

    return {
      key: sub || email || name,
      label: name || email || 'You',
      isActive: true,
    };
  } catch {
    return null;
  }
}

const NoteCollaborationToolbar: React.FC<NoteCollaborationToolbarProps> = ({
  noteId,
  confidentiality,
  onCommentsToggle,
  commentsOpen,
  onShareClick,
  onSettingsClick,
  activePresenceCount = 0,
  realtimeConnected = false,
  activeUsers = [],
}) => {
  const {
    getComments,
    fetchComments,
  } = useNoteCollaborationStore();

  const comments = getComments(noteId);

  useEffect(() => {
    if (noteId) {
      fetchComments(noteId);
    }
  }, [noteId, fetchComments]);

  const unresolvedCount = comments.filter((c) => !c.is_resolved).length;
  const isPrivileged = confidentiality !== 'public';
  const currentUserAvatar = useMemo(() => getCurrentUserAvatar(), []);

  const avatarUsers = useMemo(() => {
    const merged = new Map<string, ToolbarAvatarUser>();
    const now = Date.now();

    activeUsers.forEach((user) => {
      if (!user?.user_id) return;
      merged.set(user.user_id, {
        key: user.user_id,
        label: user.user_email || 'Collaborator',
        isActive: true,
        isChatPing: typeof user.chat_ping_until === 'number' && user.chat_ping_until > now,
      });
    });

    const remoteActive = Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label));
    if (!currentUserAvatar) return remoteActive;

    if (merged.has(currentUserAvatar.key)) {
      return remoteActive;
    }

    return [currentUserAvatar, ...remoteActive];
  }, [activeUsers, currentUserAvatar]);

  const avatarColorsByKey = useMemo(() => {
    const colorByKey: Record<string, string> = {};
    const usedIndices = new Set<number>();
    const sortedKeys = avatarUsers.map((user) => user.key).sort();

    sortedKeys.forEach((key) => {
      let colorIndex = hashKey(key) % AVATAR_COLORS.length;
      const startIndex = colorIndex;

      while (usedIndices.has(colorIndex) && usedIndices.size < AVATAR_COLORS.length) {
        colorIndex = (colorIndex + 1) % AVATAR_COLORS.length;
        if (colorIndex === startIndex) break;
      }

      usedIndices.add(colorIndex);
      colorByKey[key] = AVATAR_COLORS[colorIndex];
    });

    return colorByKey;
  }, [avatarUsers]);

  return (
    <div className="flex items-center gap-2">
      {/* Presence avatars (self + active viewers/editors) */}
      {avatarUsers.length > 0 && (
        <div className="flex items-center -space-x-1.5">
          {avatarUsers.slice(0, 4).map((user) => (
            <div
              key={user.key}
              className={`relative w-6 h-6 rounded-full text-white text-[10px] font-medium flex items-center justify-center ring-2 ${
                user.isChatPing
                  ? 'ring-amber-300 dark:ring-amber-500 animate-pulse'
                  : 'ring-white dark:ring-gray-800'
              }`}
              title={user.isChatPing ? `${user.label} sent a message` : user.label}
              style={{ backgroundColor: avatarColorsByKey[user.key] || AVATAR_COLORS[0] }}
            >
              {(user.label || '?').charAt(0).toUpperCase()}
              {user.isActive && (
                <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400 border border-white dark:border-gray-800" />
              )}
            </div>
          ))}
          {avatarUsers.length > 4 && (
            <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-[10px] font-medium flex items-center justify-center ring-2 ring-white dark:ring-gray-800">
              +{avatarUsers.length - 4}
            </div>
          )}
        </div>
      )}

      {(realtimeConnected || activePresenceCount > 0) && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-[10px] font-medium"
          title={`${activePresenceCount} active collaborator session${activePresenceCount === 1 ? '' : 's'}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${realtimeConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          <span>{activePresenceCount}</span>
        </div>
      )}

      {/* Comments toggle button */}
      <button
        onClick={onCommentsToggle}
        className={`relative flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
          commentsOpen
            ? 'bg-lex-accent-soft text-lex-accent'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title={`${unresolvedCount} unresolved comment${unresolvedCount !== 1 ? 's' : ''}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        {unresolvedCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-lex-accent text-lex-accent-contrast text-[9px] font-bold rounded-full flex items-center justify-center">
            {unresolvedCount > 9 ? '9+' : unresolvedCount}
          </span>
        )}
      </button>

      {/* Share button */}
      <button
        onClick={onShareClick}
        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Share note"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        <span>Share</span>
      </button>

      {/* Settings button */}
      <button
        onClick={onSettingsClick}
        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Collaboration settings"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317a1 1 0 011.35-.936l.813.326a1 1 0 001.046-.156l.646-.517a1 1 0 011.52.176l.732.977a1 1 0 001.01.379l1.22-.174a1 1 0 011.125 1.125l-.174 1.22a1 1 0 00.379 1.01l.977.732a1 1 0 01.176 1.52l-.517.646a1 1 0 00-.156 1.046l.326.813a1 1 0 01-.936 1.35h-1.014a1 1 0 00-.948.684l-.342 1.026a1 1 0 01-1.364.593l-.94-.47a1 1 0 00-1.026.099l-.853.64a1 1 0 01-1.45-.303l-.577-.866a1 1 0 00-.92-.45l-1.035.07a1 1 0 01-1.06-1.06l.07-1.035a1 1 0 00-.45-.92l-.866-.577a1 1 0 01-.303-1.45l.64-.853a1 1 0 00.099-1.026l-.47-.94a1 1 0 01.593-1.364l1.026-.342a1 1 0 00.684-.948V5.253z" />
          <circle cx="12" cy="12" r="3" strokeWidth={1.5} />
        </svg>
      </button>

      {/* Privilege indicator */}
      {isPrivileged && (
        <div className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded" title="This note contains privileged content">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default NoteCollaborationToolbar;
